import {
  formatDate,
  formatDateTimeIntoIsoDate,
} from "@/composables/formatUtils";
import { compact, map, max, min, orderBy, some, uniq } from "lodash-es";
import { EcolabelStatus } from "../api/ecolabelsTypes";
import type {
  Label,
  LabelProblem,
  LabelSource,
  LabelStatus,
} from "./labelsTypes";

export interface TranslatedMessage {
  key: string;
  params: Record<string, string | number | null>;
}

export interface TranslatedPill {
  key: string;
  pillClass: string;
}

export interface LabelPeriodBounds {
  start: string;
  end: string;
}

/**
 * A label with the scalar fields the list table filters on.
 *
 * @property number_text - The number, or the draft placeholder a draft shows in its place
 * @property countries - The distinct country codes of the rows, joined as the cell shows them
 */
export interface LabelListRow extends Label {
  number_text: string;
  countries: string;
}

/** A DatePicker's `update:modelValue`, which PrimeVue types for every selection mode at once. */
export type PickedDate = Date | Date[] | (Date | null)[] | null | undefined;

/** The design system's neutral pill, for a status that is settled and needs no attention. */
export const NEUTRAL_PILL_CLASS = "bg-body-emphasis text-body border";

const WHOLE_MWH_PATTERN = /^\d+$/;
const MAX_WHOLE_MWH = 2_147_483_647;

const DEVICE_PROBLEM_CODES = new Set<LabelProblem["code"]>([
  "DEVICE_DELETED",
  "DEVICE_NOT_PRE_APPROVED",
  "DEVICE_TYPE_MISSING",
  "DEVICE_TYPE_UNSUPPORTED",
]);

const DEVICE_NOT_APPROVED_KEYS: Partial<Record<EcolabelStatus, string>> = {
  [EcolabelStatus.PENDING_REVIEW]: "device_pending_review",
  [EcolabelStatus.REJECTED]: "device_rejected",
};

const STATUS_PILL_CLASSES: Record<LabelStatus, string> = {
  DRAFT: "pill-attention",
  ISSUED: NEUTRAL_PILL_CLASS,
  VOIDED: "pill-inactive",
};

const DEVICE_STATUS_PILLS: Record<EcolabelStatus, TranslatedPill> = {
  [EcolabelStatus.PRE_APPROVED]: {
    key: "ecolabels.statuses.pre_approved",
    pillClass: NEUTRAL_PILL_CLASS,
  },
  [EcolabelStatus.PENDING_REVIEW]: {
    key: "ecolabels.labels.device_statuses.pending_review",
    pillClass: "pill-attention",
  },
  [EcolabelStatus.REJECTED]: {
    key: "ecolabels.labels.device_statuses.rejected",
    pillClass: "bg-danger-subtle text-danger-emphasis",
  },
};

const MATCH_REASON_KEYS = new Set([
  "registration_id_exact",
  "registration_id_overlap",
  "registration_id_suspect_overlap",
  "name_exact",
  "name_exact_distinct_ids",
  "name_substring",
  "coords_within_250m",
]);

const FUZZY_NAME_REASON_PREFIX = "name_fuzzy_";
const ENTITY_SEPARATOR = " / ";

/**
 * A label's period as the screen shows it: the year of a full-year period, both dates of a range,
 * or an empty string while a date is missing.
 */
export function formatLabelPeriod(
  label: Pick<Label, "period_mode" | "period_start" | "period_end">
): string {
  if (!label.period_start || !label.period_end) return "";
  if (label.period_mode === "FULL_YEAR") return label.period_start.slice(0, 4);
  return formatDateRange(label.period_start, label.period_end);
}

/** Both dates of a period, or an empty string while either is missing. */
export function formatDateRange(
  start: string | null,
  end: string | null
): string {
  if (!start || !end) return "";
  return `${formatDate(start)} – ${formatDate(end)}`;
}

/**
 * The translation key and parameters of a problem code. A device that is not pre-approved reads
 * differently while it awaits review and once it is rejected.
 */
export function labelProblemMessage(problem: LabelProblem): TranslatedMessage {
  return {
    key: `ecolabels.labels.problems.${problemKey(problem)}`,
    params: problemParams(problem),
  };
}

/** The translation key and pill class of a linked or candidate device's catalog status. */
export function deviceStatusPill(status: EcolabelStatus): TranslatedPill {
  return DEVICE_STATUS_PILLS[status];
}

/**
 * The translation of one piece of detection evidence, or null for a code this screen does not know,
 * which the caller shows as it came.
 */
export function matchReasonMessage(reason: string): TranslatedMessage | null {
  if (reason.startsWith(FUZZY_NAME_REASON_PREFIX)) {
    const similarity = Number(reason.slice(FUZZY_NAME_REASON_PREFIX.length));
    return Number.isFinite(similarity)
      ? {
          key: "ecolabels.labels.reasons.name_fuzzy",
          params: { similarity: Math.round(similarity * 100) },
        }
      : null;
  }
  return MATCH_REASON_KEYS.has(reason)
    ? { key: `ecolabels.labels.reasons.${reason}`, params: {} }
    : null;
}

/** The first line of a label name, which is all a table cell has room for. */
export function labelNameFirstLine(name: string | null): string {
  return name?.split("\n")[0] ?? "";
}

/**
 * The status pill class of a label: a draft still needs issuing, an issued label is settled, a
 * voided label is inactive.
 */
export function labelStatusPillClass(status: LabelStatus): string {
  return STATUS_PILL_CLASSES[status];
}

/** The rows of the label list table, in the order of `sortLabelsForList`, which the table keeps while unsorted. */
export function labelListRows(
  labels: readonly Label[],
  draftNumber: string
): LabelListRow[] {
  return sortLabelsForList(labels).map((label) => ({
    ...label,
    number_text: label.number ?? draftNumber,
    countries: uniq(map(label.rows, "country")).join(", "),
  }));
}

/**
 * The labels in list order: drafts first, newest created first; then issued and voided labels,
 * most recently issued first.
 */
export function sortLabelsForList(labels: readonly Label[]): Label[] {
  return orderBy(
    labels,
    [
      (label) => label.issued_at === null,
      (label) => label.issued_at ?? label.created_at,
    ],
    ["desc", "desc"]
  );
}

/**
 * How many entities a label's annex names. A row several entities contributed to names them joined
 * with " / ", so the rows are counted by those parts; issued and voided labels keep their rows.
 */
export function entityCount(label: Pick<Label, "rows">): number {
  return uniq(
    label.rows.flatMap((row) => row.entity?.split(ENTITY_SEPARATOR) ?? [])
  ).length;
}

/**
 * Whether a name is what the backend derives from the sources: the beneficiary when they all name
 * one (compared ignoring case and runs of whitespace), nothing when they name several or none.
 */
export function isNameDerived(
  name: string,
  sources: readonly LabelSource[]
): boolean {
  const beneficiaries = uniq(
    compact(sources.map((source) => collapseName(source.beneficiary_name)))
  );
  const suggestion = beneficiaries.length === 1 ? beneficiaries[0] : "";
  return collapseName(name) === suggestion;
}

/** The period the backend derives from the sources: the earliest start to the latest end. */
export function derivedPeriod(
  sources: readonly LabelSource[]
): LabelPeriodBounds | null {
  const start = min(compact(sources.map((source) => source.period_start)));
  const end = max(compact(sources.map((source) => source.period_end)));
  return start === undefined || end === undefined ? null : { start, end };
}

/** Whether a period is the one derived from the sources, or empty while they give none. */
export function isPeriodDerived(
  start: string | null,
  end: string | null,
  sources: readonly LabelSource[]
): boolean {
  const derived = derivedPeriod(sources);
  if (derived === null) return start === null && end === null;
  return start === derived.start && end === derived.end;
}

/** Whether a source's statement period starts before or ends after the label's period. */
export function hasSourceOutsidePeriod(
  start: string | null,
  end: string | null,
  sources: readonly LabelSource[]
): boolean {
  if (start === null || end === null) return false;
  return some(
    sources,
    (source) =>
      (source.period_start !== null && source.period_start < start) ||
      (source.period_end !== null && source.period_end > end)
  );
}

/** The ISO date a single-date DatePicker reported, empty once its button bar cleared it. */
export function pickedIsoDate(date: PickedDate): string {
  return date instanceof Date ? formatDateTimeIntoIsoDate(date) : "";
}

/** Whether an MWh input holds a whole number of MWh, or nothing yet. */
export function isWholeMwh(text: string | undefined): boolean {
  return (
    text === undefined ||
    text === "" ||
    (WHOLE_MWH_PATTERN.test(text) && Number(text) <= MAX_WHOLE_MWH)
  );
}

/** The MWh of an input checked with `isWholeMwh`, null while it is empty. */
export function mwhOf(text: string): number | null {
  return text === "" ? null : Number(text);
}

function collapseName(name: string | null): string {
  return (name ?? "").trim().replace(/\s+/g, " ").toLowerCase();
}

function problemKey(problem: LabelProblem): string {
  const deviceStatusKey =
    problem.code === "DEVICE_NOT_PRE_APPROVED" && problem.device_status
      ? DEVICE_NOT_APPROVED_KEYS[problem.device_status]
      : undefined;
  return deviceStatusKey ?? problem.code.toLowerCase();
}

function problemParams(problem: LabelProblem): TranslatedMessage["params"] {
  if (DEVICE_PROBLEM_CODES.has(problem.code))
    return { device: problem.device_name ?? `#${problem.device_id}` };
  if (problem.code === "ATTRIBUTION_REQUIRED")
    return {
      expected: problem.expected_mwh,
      attributed: problem.attributed_mwh,
    };
  return {};
}
